/**
 * h5/src/components/toast/useToast.ts
 * 正式 Toast 系统 — composable（单例）
 *
 * API:
 *   toast.success(msg, opts?) → id
 *   toast.info(msg, opts?)    → id
 *   toast.warn(msg, opts?)    → id
 *   toast.error(msg, opts?)   → id
 *   toast.loading(msg, opts?) → id  (Infinity duration，需手动 dismiss)
 *   toast.dismiss(id)
 *   toast.clear()
 */

import { reactive } from 'vue'

// ─── 类型定义 ─────────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'info' | 'warn' | 'error' | 'loading'

export interface ToastOpts {
  /** 持续时间（ms）。success 默认 2000，error 默认 4000，loading 默认 Infinity */
  duration?: number
  /** 显示位置，默认 'top' */
  position?: 'top' | 'bottom' | 'center'
  /** 是否显示关闭按钮，默认 false */
  closable?: boolean
}

export interface ToastItem {
  id: string
  variant: ToastVariant
  message: string
  duration: number
  position: 'top' | 'bottom' | 'center'
  closable: boolean
  /** 创建时间戳（用于排序） */
  createdAt: number
}

// ─── 默认 duration 表 ────────────────────────────────────────────────────────

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 2000,
  info:    3000,
  warn:    3500,
  error:   4000,
  loading: Infinity,
}

// ─── 模块级单例状态（reactive，整个应用共享） ─────────────────────────────────

interface ToastStore {
  items: ToastItem[]
}

const _store: ToastStore = reactive({ items: [] })

// id 计数器（简单递增，非加密随机，仅用于 DOM key）
let _counter = 0

function _genId(): string {
  return `toast-${Date.now()}-${++_counter}`
}

// 定时器映射（id → timer handle）
const _timers = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * 添加一条 toast 并自动调度过期
 */
function _add(variant: ToastVariant, message: string, opts: ToastOpts = {}): string {
  const id = _genId()
  const duration = opts.duration ?? DEFAULT_DURATION[variant]
  const position = opts.position ?? 'top'
  const closable = opts.closable ?? false

  _store.items.push({
    id,
    variant,
    message,
    duration,
    position,
    closable,
    createdAt: Date.now(),
  })

  // 有限时长才调度自动过期
  if (isFinite(duration) && duration > 0) {
    const timer = setTimeout(() => {
      _dismiss(id)
    }, duration)
    _timers.set(id, timer)
  }

  return id
}

function _dismiss(id: string): void {
  const idx = _store.items.findIndex((item) => item.id === id)
  if (idx !== -1) {
    _store.items.splice(idx, 1)
  }
  const timer = _timers.get(id)
  if (timer !== undefined) {
    clearTimeout(timer)
    _timers.delete(id)
  }
}

function _clear(): void {
  // 清除所有定时器
  for (const [, timer] of _timers) {
    clearTimeout(timer)
  }
  _timers.clear()
  _store.items.splice(0, _store.items.length)
}

// ─── 公共 composable ──────────────────────────────────────────────────────────

/**
 * useToast — 全局 Toast 系统入口
 *
 * 任何组件或 composable 调用此函数都操作同一个 reactive 队列。
 */
export function useToast() {
  return {
    /** toast 队列（只读，供 ToastContainer 渲染） */
    items: _store.items as readonly ToastItem[],

    success(msg: string, opts?: ToastOpts): string {
      return _add('success', msg, opts)
    },
    info(msg: string, opts?: ToastOpts): string {
      return _add('info', msg, opts)
    },
    warn(msg: string, opts?: ToastOpts): string {
      return _add('warn', msg, opts)
    },
    error(msg: string, opts?: ToastOpts): string {
      return _add('error', msg, opts)
    },
    loading(msg: string, opts?: ToastOpts): string {
      return _add('loading', msg, opts)
    },
    dismiss(id: string): void {
      _dismiss(id)
    },
    clear(): void {
      _clear()
    },
  }
}

// 导出内部 store 供 ToastContainer 直接订阅（避免每次 useToast() 创建新对象引用）
export { _store as _toastStore }
